import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export const YouTubeCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuthStore();
  const code = searchParams.get("code");
  const error = searchParams.get("error");
  const hasCalledRef = useRef(false);

  useEffect(() => {
    const finalizeConnection = async () => {
      // Prevent double execution within the same component instance
      if (hasCalledRef.current) return;
      hasCalledRef.current = true;

      const returnPath = localStorage.getItem("oauth_return_path") || "/dashboard";
      const isEarlyAccess = returnPath === "/"; // Landing page implies early access flow here
      localStorage.removeItem("oauth_return_path"); // Clean up

      if (error) {
        toast.error(`Connection failed: ${error}`);
        navigate(returnPath);
        return;
      }

      if (!code) {
        toast.error("No authorization code received");
        navigate(returnPath);
        return;
      }

      try {
        if (isEarlyAccess) {
          // For early access, we finalize but don't bind to a user session yet.
          // We just want the channel info to pass back to the landing page.
          const channelInfo = await apiClient.finalizeEarlyAccessYouTube(code);

          // Store the result temporarily so the landing page can pick it up
          sessionStorage.setItem("early_access_youtube_data", JSON.stringify(channelInfo));
          toast.success("YouTube channel connected!");
          navigate(returnPath);
        } else {
          await apiClient.finalizeYouTubeConnection(code);
          toast.success("YouTube channel connected successfully!");
          // Refresh auth store to get the new user data (with youtube_data)
          await checkAuth();
          navigate(returnPath);
        }
      } catch (err: any) {
        console.error("Connection finalization error:", err);

        // If the error is "Failed to exchange authorization code" (400),
        // it might be a race condition from Strict Mode where the first call succeeded
        // but this second call failed. We should check if the user is actually connected.
        if (!isEarlyAccess && (err.message?.includes("Failed to exchange authorization code") || err.message?.includes("400"))) {
          console.log("Possible race condition detected, checking user status...");
          try {
            // Wait a brief moment for the DB to update from the first successful call
            await new Promise(resolve => setTimeout(resolve, 1000));
            const updatedUser = await checkAuth();

            if (updatedUser?.youtube_data) {
              console.log("User is connected despite error, proceeding.");
              toast.success("YouTube channel connected successfully!");
              navigate(returnPath);
              return;
            }
          } catch (checkErr) {
            console.error("Failed to verify user status:", checkErr);
          }
        }

        // If connection failed, clear the session flag so calling pages don't assume success
        sessionStorage.removeItem("project_connecting_youtube");

        toast.error(err.message || "Failed to finalize connection");
        navigate(returnPath);
      }
    };

    finalizeConnection();
  }, [code, error, navigate, checkAuth]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
      <h2 className="text-xl font-semibold">Connecting your channel...</h2>
      <p className="text-muted-foreground">Please wait while we finalize the setup.</p>
    </div>
  );
};

export default YouTubeCallback;